const CONDITION_LEVELS = ["ideal", "good", "regular", "poor"];
const MARGIN_DEGREES = 22;

export const CARDINAL_DIRECTIONS = [
  { code: "N", displayCode: "N", name: "Norte", center: 0 },
  { code: "NE", displayCode: "NE", name: "Nordeste", center: 45 },
  { code: "E", displayCode: "L", name: "Leste", center: 90 },
  { code: "SE", displayCode: "SE", name: "Sudeste", center: 135 },
  { code: "S", displayCode: "S", name: "Sul", center: 180 },
  { code: "SW", displayCode: "SO", name: "Sudoeste", center: 225 },
  { code: "W", displayCode: "O", name: "Oeste", center: 270 },
  { code: "NW", displayCode: "NO", name: "Noroeste", center: 315 },
];

const asNullableNumber = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeAngle = (angle) => ((angle % 360) + 360) % 360;
const directionByCode = (code) => CARDINAL_DIRECTIONS.find((direction) => direction.code === code);

export function directionToDegrees(code) {
  return directionByCode(code)?.center ?? null;
}

export function inferDirectionsFromLabel(label) {
  if (typeof label !== "string") return [];
  const normalized = label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase();
  const compoundDirections = [
    ["ESE", ["E", "SE"]], ["ENE", ["NE", "E"]], ["SSE", ["SE", "S"]], ["SSO", ["S", "SW"]],
    ["OSO", ["SW", "W"]], ["ONO", ["W", "NW"]], ["NNO", ["NW", "N"]], ["NNE", ["N", "NE"]],
  ];
  const compound = compoundDirections.find(([alias]) => new RegExp(`\\b${alias}\\b`).test(normalized));
  if (compound) return compound[1];

  const aliases = [
    ["NOROESTE", "NW"], ["NORDESTE", "NE"], ["SUDOESTE", "SW"], ["SUDESTE", "SE"],
    ["NORTE", "N"], ["LESTE", "E"], ["OESTE", "W"], ["SUL", "S"],
    ["NO", "NW"], ["NE", "NE"], ["SO", "SW"], ["SE", "SE"], ["N", "N"], ["L", "E"], ["O", "W"], ["S", "S"],
  ];
  const matches = aliases
    .map(([alias, code]) => ({ code, index: normalized.search(new RegExp(`\\b${alias}\\b`)) }))
    .filter(({ index }) => index >= 0)
    .sort((left, right) => left.index - right.index)
    .map(({ code }) => code);
  return [...new Set(matches)];
}

export function groupAdjacentDirections(selectedDirections = []) {
  const selectedIndexes = [...new Set(selectedDirections)]
    .map((code) => CARDINAL_DIRECTIONS.findIndex((direction) => direction.code === code))
    .filter((index) => index >= 0)
    .sort((left, right) => left - right);
  if (selectedIndexes.length === 0) return [];

  const groups = selectedIndexes.reduce((result, index) => {
    const lastGroup = result[result.length - 1];
    if (lastGroup && index === lastGroup[lastGroup.length - 1] + 1) {
      lastGroup.push(index);
    } else {
      result.push([index]);
    }
    return result;
  }, []);

  if (groups.length > 1 && groups[0][0] === 0 && groups[groups.length - 1].at(-1) === CARDINAL_DIRECTIONS.length - 1) {
    const circularGroup = [...groups.at(-1), ...groups[0]];
    return [circularGroup, ...groups.slice(1, -1)];
  }

  return groups;
}

export function calculateRangeForGroup(indexes) {
  if (!indexes?.length) return null;
  const first = indexes[0];
  const last = indexes[indexes.length - 1];
  const min_deg = normalizeAngle(CARDINAL_DIRECTIONS[first].center - MARGIN_DEGREES);
  const max_deg = normalizeAngle(CARDINAL_DIRECTIONS[last].center + MARGIN_DEGREES);
  const span = (max_deg - min_deg + 360) % 360;
  return {
    min_deg,
    max_deg,
    center_deg: normalizeAngle(min_deg + span / 2),
    directions: indexes.map((index) => CARDINAL_DIRECTIONS[index].code),
  };
}

const rangeText = ({ min_deg, max_deg }) => `${min_deg}°–${max_deg}°`;

export function calculateDirectionSelection(selectedDirections = []) {
  const selected = selectedDirections.filter((code, index) => directionByCode(code) && selectedDirections.indexOf(code) === index);
  const groups = groupAdjacentDirections(selected);
  const ranges = groups.map(calculateRangeForGroup);
  const primary = ranges.find((range) => range.directions.includes(selected[0]));
  const alternatives = ranges.filter((range) => range !== primary);
  const label = selected.join("-");
  const original = primary
    ? `${label} (${rangeText(primary)}${alternatives.length ? `; alternativas: ${alternatives.map(rangeText).join(", ")}` : ""})`
    : "";
  return {
    label,
    center_deg: primary?.center_deg ?? null,
    min_deg: primary?.min_deg ?? null,
    max_deg: primary?.max_deg ?? null,
    ...(alternatives.length ? { alt_ranges: alternatives.map(({ min_deg, max_deg }) => ({ min_deg, max_deg })) } : {}),
    original,
  };
}

const directionOriginal = ({ label, center_deg, min_deg, max_deg, alt_ranges }) => {
  const center = asNullableNumber(center_deg);
  const min = asNullableNumber(min_deg);
  const max = asNullableNumber(max_deg);
  const range = min !== null && max !== null ? `${min}–${max}°` : min !== null ? `≥${min}°` : max !== null ? `≤${max}°` : "";
  const alternatives = Array.isArray(alt_ranges) ? alt_ranges.filter((item) => asNullableNumber(item?.min_deg) !== null && asNullableNumber(item?.max_deg) !== null).map((item) => `${item.min_deg}–${item.max_deg}°`) : [];
  const degrees = [center !== null ? `${center}°` : "", range, alternatives.length ? `alternativas: ${alternatives.join(", ")}` : ""].filter(Boolean).join("; ");
  return label && degrees ? `${label} (${degrees})` : label || degrees;
};

const rangeOriginal = (minValue, maxValue, unit) => {
  const min = asNullableNumber(minValue);
  const max = asNullableNumber(maxValue);
  if (min !== null && max !== null) return `${min}–${max} ${unit}`;
  if (min !== null) return `≥${min} ${unit}`;
  if (max !== null) return `≤${max} ${unit}`;
  return "";
};

const emptyDirection = (isWind = false) => ({
  label: "",
  center_deg: "",
  min_deg: "",
  max_deg: "",
  selected_directions: [],
  directions_touched: false,
  ...(isWind ? { calm_tolerant: false, onshore: "" } : {}),
});

export function createEmptyConditionLevel() {
  return {
    size: { min_m: "", max_m: "" },
    swell_direction: emptyDirection(),
    period: { min_s: "", max_s: "" },
    wind: emptyDirection(true),
  };
}

export function createEmptySpotInfo() {
  return {
    season: "", spot_description: "", observations: "", notes: "", aliases: "",
    conditions: CONDITION_LEVELS.reduce((result, level) => {
      result[level] = null;
      return result;
    }, {}),
  };
}

const directionsFromRange = (source = {}) => {
  const min = asNullableNumber(source.min_deg);
  const max = asNullableNumber(source.max_deg);
  if (min === null || max === null) {
    const center = asNullableNumber(source.center_deg);
    if (center === null) return [];

    return [CARDINAL_DIRECTIONS.reduce((closest, direction) => {
      const distance = Math.min(Math.abs(direction.center - center), 360 - Math.abs(direction.center - center));
      const closestDistance = Math.min(Math.abs(closest.center - center), 360 - Math.abs(closest.center - center));
      return distance < closestDistance ? direction : closest;
    }).code];
  }
  const isWithinRange = (angle) => min <= max ? angle >= min && angle <= max : angle >= min || angle <= max;
  return CARDINAL_DIRECTIONS.filter((direction) => isWithinRange(direction.center)).map((direction) => direction.code);
};

const hydrateDirection = (source = {}, empty) => {
  const safeSource = source || {};
  const inferred = inferDirectionsFromLabel(safeSource.label);
  return {
    ...empty,
    ...safeSource,
    selected_directions: inferred.length ? inferred : directionsFromRange(safeSource),
    directions_touched: false,
  };
};

export function hydrateSpotInfo(camera = {}) {
  const empty = createEmptySpotInfo();
  const description = camera.description && typeof camera.description === "object" && !Array.isArray(camera.description) ? camera.description : {};
  const conditions = description.conditions ?? camera.conditions ?? {};
  const aliases = description.aliases ?? camera.aliases;
  return {
    ...empty,
    season: description.season ?? camera.season ?? "",
    spot_description: description.spot_description ?? camera.spot_description ?? "",
    observations: description.observations ?? camera.observations ?? "",
    notes: description.notes ?? camera.notes ?? "",
    aliases: Array.isArray(aliases) ? aliases.join(", ") : "",
    conditions: CONDITION_LEVELS.reduce((result, level) => {
      const source = conditions[level];
      if (source === null || source === undefined) {
        result[level] = null;
        return result;
      }
      const levelEmpty = createEmptyConditionLevel();
      result[level] = {
        size: { ...levelEmpty.size, ...(source.size || {}) },
        swell_direction: hydrateDirection(source.swell_direction, levelEmpty.swell_direction),
        period: { ...levelEmpty.period, ...(source.period || {}) },
        wind: hydrateDirection(source.wind, levelEmpty.wind),
      };
      result[level].wind.calm_tolerant = Boolean(result[level].wind.calm_tolerant);
      result[level].wind.onshore = typeof source.wind?.onshore === "boolean" ? String(source.wind.onshore) : "";
      return result;
    }, {}),
  };
}

const validAltRanges = (altRanges) => (Array.isArray(altRanges) ? altRanges : [])
  .map((range) => ({ min_deg: asNullableNumber(range?.min_deg), max_deg: asNullableNumber(range?.max_deg) }))
  .filter((range) => range.min_deg !== null && range.max_deg !== null);

const serializeDirection = (source) => {
  const direction = source.directions_touched ? { ...source, ...calculateDirectionSelection(source.selected_directions) } : source;
  const result = {
    original: direction.original || directionOriginal(direction),
    label: direction.label || "",
    center_deg: asNullableNumber(direction.center_deg),
    min_deg: asNullableNumber(direction.min_deg),
    max_deg: asNullableNumber(direction.max_deg),
  };
  const alt_ranges = validAltRanges(direction.alt_ranges);
  if (alt_ranges.length) result.alt_ranges = alt_ranges;
  return result;
};

export function serializeSpotInfo(spotInfo) {
  const serializeLevel = (level) => {
    const source = spotInfo.conditions[level];
    if (source === null || source === undefined) return null;
    const wind = { ...serializeDirection(source.wind), calm_tolerant: Boolean(source.wind.calm_tolerant) };
    if (source.wind.onshore !== "") wind.onshore = source.wind.onshore === "true";
    return {
      size: { original: rangeOriginal(source.size.min_m, source.size.max_m, "m"), min_m: asNullableNumber(source.size.min_m), max_m: asNullableNumber(source.size.max_m) },
      swell_direction: serializeDirection(source.swell_direction),
      period: { original: rangeOriginal(source.period.min_s, source.period.max_s, "s"), min_s: asNullableNumber(source.period.min_s), max_s: asNullableNumber(source.period.max_s) },
      wind,
    };
  };

  return {
    season: spotInfo.season, spot_description: spotInfo.spot_description, observations: spotInfo.observations, notes: spotInfo.notes,
    aliases: spotInfo.aliases.split(",").map((alias) => alias.trim()).filter(Boolean),
    conditions: CONDITION_LEVELS.reduce((result, level) => { result[level] = serializeLevel(level); return result; }, {}),
  };
}
